import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Store, StoreGroup } from '../api/types';
import { AppHeader } from '../components/AppHeader';
import StoreCard from '../components/StoreCard';
import OrgManagePanel from '../components/OrgManagePanel';
import { useAuth } from '../store/auth';
import { useStoreSlice } from '../store/storeRegistry';

export default function Stores() {
  const me = useAuth((s) => s.me);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  // Super admins may enter any org's view (spec §A6); everyone else is pinned to their own.
  const orgId = me?.role === 'admin' ? params.get('org') ?? null : me?.org?.id ?? null;
  const canManage = me?.role === 'admin' ? orgId !== null : me?.org_role === 'owner';

  const storesQ = useQuery({ queryKey: ['stores'], queryFn: api.listStores });
  // Admins viewing an org need its groups/users; org users already have groups in /api/me.
  const orgQ = useQuery({
    queryKey: ['org', orgId], queryFn: () => api.getOrg(orgId!), enabled: me?.role === 'admin' && !!orgId,
  });
  const groups: StoreGroup[] = me?.role === 'admin' ? orgQ.data?.org.groups ?? [] : me?.groups ?? [];

  const visible: Store[] = useMemo(() => {
    const all = storesQ.data ?? [];
    return orgId ? all.filter((s) => s.org_id === orgId) : all;
  }, [storesQ.data, orgId]);

  const sections = useMemo(() => {
    const byGroup = new Map<string | null, Store[]>();
    for (const s of visible) byGroup.set(s.group_id ?? null, [...(byGroup.get(s.group_id ?? null) ?? []), s]);
    const ordered: Array<{ gid: string | null; name: string; stores: Store[] }> = groups
      .filter((g) => byGroup.has(g.id))
      .map((g) => ({ gid: g.id, name: g.name, stores: byGroup.get(g.id)! }));
    // groups the me/org payload doesn't know (stale cache) still render, by id
    for (const [gid, list] of byGroup) {
      if (gid && !groups.some((g) => g.id === gid)) ordered.push({ gid, name: gid, stores: list });
    }
    if (byGroup.has(null)) ordered.push({ gid: null, name: 'Ungrouped', stores: byGroup.get(null)! });
    return ordered;
  }, [visible, groups]);

  function open(storeId: string, to: string) {
    useStoreSlice.getState().setSelectedStoreId(storeId);
    qc.invalidateQueries({ queryKey: ['configs'] });
    qc.invalidateQueries({ queryKey: ['samples'] });
    // Parity with StoreSelector.pick(): same logical action (switch the
    // active store), so the singular ['config'] cache must invalidate too.
    qc.invalidateQueries({ queryKey: ['config'] });
    nav(to);
  }

  return (
    <div className="min-h-screen bg-ff-ivory relative" data-testid="stores-page">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />
      <AppHeader active="stores" />
      <main className="relative mx-auto max-w-[1240px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-16">
        <div className="border-b border-ff-line pb-10">
          <p className="ff-kicker mb-4">{orgQ.data?.org.name ?? me?.org?.name ?? 'Stores'}</p>
          <h1 className="ff-page-title text-[clamp(3.25rem,7vw,5.5rem)] leading-[0.92]">
            Your <span className="block text-ff-orange">stores.</span>
          </h1>
        </div>

        {storesQ.isLoading && <p className="mt-10 text-sm text-ff-mute">Loading…</p>}
        {storesQ.isSuccess && visible.length === 0 && (
          <div className="mt-16 rounded-2xl border border-dashed border-ff-line bg-white/70 px-6 py-14 text-center" data-testid="stores-empty">
            <p className="font-display text-2xl font-bold text-ff-ink">No stores assigned to you yet.</p>
            <p className="mt-2 text-sm text-ff-mute">Ask your organization owner to add you to a store group.</p>
          </div>
        )}

        {sections.map((sec) => (
          <section key={sec.gid ?? 'ungrouped'} className="mt-12" data-testid={`group-section-${sec.gid ?? 'ungrouped'}`}>
            <p className="ff-kicker mb-4">{sec.name}</p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {sec.stores.map((s) => (
                <StoreCard key={s.id} store={s} groupName={sec.gid ? sec.name : null}
                  onOpenDesigns={() => open(s.id, '/')} onOpenQa={() => open(s.id, '/cutout-qa')} />
              ))}
            </div>
          </section>
        ))}

        {canManage && orgId && (
          <section className="mt-16" data-testid="org-manage">
            <OrgManagePanel orgId={orgId} />
          </section>
        )}
      </main>
    </div>
  );
}
